import { ApiClient } from "../config/axiosConfig";
import { IPaginatedResponse } from "../models/Pagination";
import {
  ICreateProductionProject,
  ICreateProductionProjectDocuments,
  ICreateProductionProjectMember,
  ICreateProductionProjectPhase,
  ICreateProductionProjectPhaseMilestone,
  ICreateProductionPurchaseRequest,
  IProductionListItemAwb,
  IProductionProject,
  IProductionProjectDocumentDownloadHistory,
  IProductionProjectDocuments,
  IProductionProjectMember,
  IProductionProjectOverview,
  IProductionProjectPhaseMilestone,
  IProductionProjectPhases,
  IProductionPurchaseRequest,
  IUpdateProductionProject,
  IUpdateProductionProjectMember,
  IUpdateProductionProjectPhase,
  IUpdateProductionPurchaseRequest,
} from "../models/Production";
import { IFilterResearch } from "../models/Research";

export const productionService = {
  getAllProductionProject: async (query: IFilterResearch) => {
    const response = await ApiClient.GET<
      IPaginatedResponse<IProductionProject>
    >("/prd-projects", query);
    return response;
  },
  createProductionProject: async (data: ICreateProductionProject) => {
    const response = await ApiClient.POST<IProductionProject>(
      "/prd-projects",
      data,
    );
    return response;
  },
  updateProductionProject: async (
    id: number,
    data: IUpdateProductionProject,
  ) => {
    const response = await ApiClient.PUT<IProductionProject>(
      `/prd-projects/${id}`,
      data,
    );
    return response;
  },
  importListRequest: async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    const res = await ApiClient.POST(
      "/prd-purchase-requests/import",
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );
    return res;
  },
  getProductionProjectByAlias: async (alias: string, partNumber?: string) => {
    const params = partNumber ? { partNumber } : {};
    const response = await ApiClient.GET<IProductionProject>(
      `/prd-projects/${alias}`,
      params,
    );
    return response;
  },
  createProductionProjectMember: async (
    data: ICreateProductionProjectMember,
  ) => {
    const response = await ApiClient.POST<IProductionProjectMember>(
      "/prd-project-members",
      data,
    );
    return response;
  },
  getProductionProjectMember: async (id: number) => {
    const response = await ApiClient.GET<IProductionProjectMember[]>(
      `/prd-project-members/${id}`,
    );
    return response;
  },
  updateProductionProjectMember: async (
    id: number,
    data: IUpdateProductionProjectMember,
  ) => {
    const response = await ApiClient.PUT<IProductionProjectMember>(
      `/prd-project-members/${id}`,
      data,
    );
    return response;
  },
  deleteProductionProjectMember: async (id: number) => {
    const response = await ApiClient.DELETE(`/prd-project-members/${id}`);
    return response;
  },
  getProductionPurchaseRequest: async (
    prdProjectId: number,
    partNumber?: string,
  ) => {
    const params = partNumber ? { partNumber } : {};
    const response = await ApiClient.GET<IProductionPurchaseRequest[]>(
      `/prd-purchase-requests/${prdProjectId}`,
      params,
    );
    return response;
  },
  createProductionPurchaseRequest: async (
    data: ICreateProductionPurchaseRequest,
  ) => {
    const response = await ApiClient.POST<IProductionPurchaseRequest>(
      `/prd-purchase-requests`,
      data,
    );
    return response;
  },
  updateProductionPurchaseRequest: async (
    id: number,
    data: IUpdateProductionPurchaseRequest,
  ) => {
    const response = await ApiClient.PUT<IProductionPurchaseRequest>(
      `/prd-purchase-requests/${id}`,
      data,
    );
    return response;
  },
  requestProductionPurchaseRequestToDirector: async (id: number) => {
    const response = await ApiClient.PATCH<IProductionPurchaseRequest>(
      `/prd-purchase-requests/${id}/request-director`,
    );
    return response;
  },
  approveProductionPurchaseRequestByDirector: async (id: number) => {
    const response = await ApiClient.PATCH<IProductionPurchaseRequest>(
      `/prd-purchase-requests/${id}/approve-director`,
    );
    return response;
  },
  getProductionListItemAwbs: async (id: number) => {
    const response = await ApiClient.GET<IProductionListItemAwb[]>(
      `/prd-list-item-awbs/${id}`,
    );
    return response;
  },
  getProductionProjectOverview: async () => {
    const response = await ApiClient.GET<IProductionProjectOverview>(
      `/prd-projects/overview`,
    );
    return response;
  },
  getProductionProjectPhases: async (prdProjectId: number) => {
    const response = await ApiClient.GET<IProductionProjectPhases[]>(
      `/prd-project-phases/${prdProjectId}`,
    );
    return response;
  },
  updateProductionProjectPhases: async (
    id: number,
    data: IUpdateProductionProjectPhase,
  ) => {
    const response = await ApiClient.PUT<IProductionProjectPhases>(
      `/prd-project-phases/${id}`,
      data,
    );
    return response;
  },
  createProductionProjectPhases: async (
    data: ICreateProductionProjectPhase,
  ) => {
    const response = await ApiClient.POST<IProductionProjectPhases>(
      `/prd-project-phases`,
      data,
    );
    return response;
  },
  deleteProductionProjectPhases: async (phaseId: number) => {
    const response = await ApiClient.DELETE(`/prd-project-phases/${phaseId}`);
    return response;
  },
  deleteProductionProjectPhaseMilestone: async (milestoneId: number) => {
    const response = await ApiClient.DELETE(
      `/prd-project-phase-milestones/${milestoneId}`,
    );
    return response;
  },
  createProductionProjectPhaseMilestone: async (
    data: ICreateProductionProjectPhaseMilestone,
  ) => {
    const response = await ApiClient.POST<IProductionProjectPhaseMilestone>(
      `/prd-project-phase-milestones`,
      data,
    );
    return response;
  },
  uploadProductionDocument: async (data: ICreateProductionProjectDocuments) => {
    const response = await ApiClient.POST<IProductionProjectDocuments>(
      `/prd-project-documents`,
      data,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response;
  },
  getDocumentByProjectId: async (projectId: number) => {
    const response = await ApiClient.GET<IProductionProjectDocuments[]>(
      `/prd-project-documents/${projectId}`,
    );
    return response;
  },
  // downloadDocument: async (fileUrl: string) => {
  //   const response = await ApiClient.GET(
  //     `/local-storage/download?path=${fileUrl}`,
  //     {},
  //     {
  //       responseType: "blob",
  //     },
  //   );
  //   return response;
  // },

  deleteDocumentProject: async (documentId: number) => {
    const response = await ApiClient.DELETE(
      `/prd-project-documents/${documentId}`,
    );
    return response;
  },
  downloadDocumentProject: async (documentId: number) => {
    const response = await ApiClient.GET<string>(
      `/prd-project-documents/${documentId}/download/`,
    );
    return response;
  },
  getProductionDownloadHistoryDocument: async (documentId: number) => {
    const response = await ApiClient.GET<
      IProductionProjectDocumentDownloadHistory[]
    >(`/prd-project-document-activities/${documentId}`);
    return response;
  },
};
